import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { PhoneCall, MessageSquarePlus, Ticket as TicketIcon, Plus, Lock } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { useMeState } from '../../lib/me.js'
import { asList, callTs } from '../../lib/datetime.js'
import {
  Badge, Button, DataTable, ErrorState, Loading, PageHeader, Segmented, Tabs,
  fmtDate, useTableState,
} from '../../kit'
import { lockState, LOCK_META } from '../../lib/recallLock.js'
import { tDateFollowup, tFollowupRel, REL_TONES, tTags, tDone, normalizeTagCatalog } from '../../lib/ticket.js'
import RecallNotesSection from '../../components/recall/RecallNotesSection.jsx'
import { usePatientLive } from '../../lib/patientLive.js'
import PhoneLogTable from '../../components/phone/PhoneLogTable.jsx'
import PatientNotes from '../../components/patient/PatientNotes.jsx'
import TicketDrawer from '../../components/ticket/TicketDrawer.jsx'

// ── /patient/:id/follow-ups — ติดตาม ─────────────────────────────────────────
//
// Everything the clinic has said TO, ABOUT, or ON THE PHONE WITH this patient.
// Three of the four already existed only inside the recall worklist drawer,
// reachable only if the patient happened to surface on a worklist.
//
// The four are not the same KIND of thing, and the layout says so:
//   โน้ต   = standing STATE  → pinned card at the top, always present
//   Recall = events          ┐
//   Ticket = open work       ├ three equal tabs
//   การโทร = raw telephony   ┘
//
// There is deliberately NO merged "ทั้งหมด" timeline. The three sources carry
// incompatible time semantics — recall.date is a past date, ticket.date_followup
// is a FUTURE due date while created_at is epoch ms, and phone.start_stamp is a
// Bangkok-local epoch — so any single sort order lies about at least one of them.
// Their affordances diverge too (inline edit / a drawer / an <audio>).

const LOCK_TONE = { free: 'slate', mine: 'amber', other: 'rose' }
const TICKET_MODES = [{ value: 'not_done', label: 'ยังไม่เสร็จ' }, { value: 'done', label: 'เสร็จแล้ว' }]

// The ticket list. Server order is kept until a header is clicked.
const TICKET_COLUMNS = [
  {
    key: 'details', label: 'Ticket',
    render: (t) => {
      const tags = tTags(t)
      return (
        <div className="min-w-0">
          <div className="text-slate-800 break-words">{t.details || t.detail || `Ticket #${t.id}`}</div>
          {tags.length > 0 && (
            <div className="mt-1 flex items-center gap-1 flex-wrap">
              {tags.map((tag) => <Badge key={tag}>{tag}</Badge>)}
            </div>
          )}
        </div>
      )
    },
  },
  {
    key: 'followup', label: 'กำหนดติดตาม', width: '200px', sortable: true, defaultDir: 'asc',
    sortValue: (t) => tDateFollowup(t) || '',
    render: (t) => {
      const rel = tFollowupRel(t)
      return (
        <div className="text-[12.5px]">
          {/* Urgency is suppressed on a done ticket — "เลยกำหนด 300 วัน" on something
              already finished is noise. tFollowupRel already returns null for those. */}
          {rel && <div className={`font-medium ${REL_TONES[rel.tone].text}`}>{rel.label}</div>}
          <div className="text-slate-400 tabular-nums">
            {fmtDate(tDateFollowup(t))}
            {tDone(t) && <span className="text-emerald-600 font-medium"> · เสร็จแล้ว</span>}
          </div>
        </div>
      )
    },
  },
  {
    key: 'follow_up', label: 'ติดตาม', align: 'right', width: '90px', hideBelow: 'sm',
    sortable: true, defaultDir: 'desc',
    sortValue: (t) => (Array.isArray(t.follow_up) ? t.follow_up.length : 0),
    render: (t) => (Array.isArray(t.follow_up) && t.follow_up.length > 0
      ? <span className="text-slate-500">{t.follow_up.length}</span>
      : <span className="text-slate-300">—</span>),
  },
]

const TABS = [
  // Distinct icons: Recall and การโทร both being PhoneCall made two of three tabs
  // indistinguishable at a glance. MessageSquarePlus matches RecallNotesSection's
  // own section icon.
  { key: 'recall', label: 'Recall', Icon: MessageSquarePlus },
  { key: 'ticket', label: 'Ticket', Icon: TicketIcon },
  { key: 'phone',  label: 'การโทร', Icon: PhoneCall },
]

export default function PatientFollowUps() {
  const { id } = useParams()
  const { patient, setPatient, live } = useOutletContext() || {}
  const { me } = useMeState()

  const [tab, setTab] = useState('recall')
  const [tickets, setTickets] = useState(null)   // null = loading, undefined-ish handled below
  const [ticketErr, setTicketErr] = useState('')
  const [ticketTotal, setTicketTotal] = useState(null)
  const [ticketMode, setTicketMode] = useState('not_done')
  const [phones, setPhones] = useState(null)
  const [phoneErr, setPhoneErr] = useState('')
  const [phoneTotal, setPhoneTotal] = useState(null)
  // { mode: 'view'|'create', ticketId } — the drawer's own contract.
  const [drawer, setDrawer] = useState(null)
  const [branches, setBranches] = useState([])
  const [tagCatalog, setTagCatalog] = useState([])

  // Sequence guards. This component stays mounted across /patient/1/follow-ups →
  // /patient/2/follow-ups, so patient 1's answer can land AFTER patient 2's and put
  // one patient's tickets and call log under another patient's name. The same guard
  // also covers the ยังไม่เสร็จ/เสร็จแล้ว toggle, where two fast switches can resolve
  // out of order and show the list the button says is off.
  const ticketReq = useRef(0)
  const phoneReq = useRef(0)

  // `quiet` = a realtime refetch: keep the current list on screen instead of
  // dropping it to a spinner while the new one loads.
  const loadTickets = useCallback((quiet = false) => {
    if (!id) return
    const seq = ++ticketReq.current
    if (!quiet) { setTickets(null); setTicketTotal(null) }
    setTicketErr('')
    // Explicit per_page: the proxy hard-defaults to 30, so a count taken from a
    // page length would be a completeness claim the fetch cannot support. The
    // envelope's itemsTotal is the only honest number.
    apiGet('/api/ticket', { patient_id: id, mode: ticketMode, per_page: 100, page: 1 })
      .then((r) => {
        if (seq !== ticketReq.current) return
        setTickets(asList(r)); setTicketTotal(Number(r?.itemsTotal ?? asList(r).length))
      })
      .catch((e) => {
        if (seq !== ticketReq.current) return
        setTickets([]); setTicketTotal(null); setTicketErr(e?.message || 'โหลด Ticket ไม่สำเร็จ')
      })
  }, [id, ticketMode])

  const loadPhones = useCallback((quiet = false) => {
    if (!id) return
    const seq = ++phoneReq.current
    if (!quiet) { setPhones(null); setPhoneTotal(null) }
    setPhoneErr('')
    apiGet('/api/phone', { patient_id: id, per_page: 100 })
      .then((r) => {
        if (seq !== phoneReq.current) return
        const l = asList(r); setPhones(l); setPhoneTotal(Number(r?.itemsTotal ?? l.length))
      })
      .catch((e) => {
        if (seq !== phoneReq.current) return
        setPhones([]); setPhoneTotal(null); setPhoneErr(e?.message || 'โหลดประวัติการโทรไม่สำเร็จ')
      })
  }, [id])

  // Both fire independently — one failing must not blank the other.
  useEffect(() => { loadTickets() }, [loadTickets])
  useEffect(() => { loadPhones() }, [loadPhones])

  // Live: tickets, the call log, and โน้ต Recall. `recallTick` is handed to
  // RecallNotesSection, which owns its own list.
  const [recallTick, setRecallTick] = useState(0)
  usePatientLive(live, ['ticket'], () => loadTickets(true))
  usePatientLive(live, ['call'], () => loadPhones(true))
  usePatientLive(live, ['recall'], () => setRecallTick((t) => t + 1))

  // This component stays mounted across /patient/1/follow-ups → /patient/2/follow-ups,
  // so a ticket drawer left open would sit over the new patient still showing (and
  // still able to save) the old one's ticket.
  useEffect(() => { setDrawer(null); setTab('recall'); setTicketMode('not_done') }, [id])

  // Lookup tables the ticket drawer needs (branch names for its appointment card,
  // the tag catalog for TagPicker). Cached, so this costs nothing after the first
  // page in the session that asked for them.
  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(asList(r))).catch(() => {})
    cachedGet('/api/ticket/tag').then((r) => setTagCatalog(normalizeTagCatalog(r))).catch(() => {})
  }, [])

  const ticketTable = useTableState(tickets || [], TICKET_COLUMNS, { pageSize: 20 })

  const phoneRows = useMemo(
    () => (phones || []).slice().sort((a, b) => callTs(b) - callTs(a)), [phones])

  // Three distinct states, and the tab has to tell them apart:
  //   undefined → this tab shows no count at all (Recall)
  //   'loading' → nothing yet, don't render a number
  //   null      → the fetch FAILED. Renders '—', never 0 — an errored list that
  //               reads as empty is the one bug this page must not ship.
  const counts = {
    recall: undefined,
    ticket: ticketErr ? null : (tickets === null ? 'loading' : ticketTotal),
    phone: phoneErr ? null : (phones === null ? 'loading' : phoneTotal),
  }

  // The recall lock lives on `patient` (lock / lock_by / lock_timestamp), so the
  // outlet context already carries it — free. Read-only: claiming stays on the
  // worklist, where the one-at-a-time rule is enforced.
  const lock = lockState(patient, me?.employee_id)

  // The count is a CountBadge; a failed fetch sets `badgeUnknown`, which reads '—', so
  // "failed" never looks like zero (CountBadge draws nothing for 0 / still loading).
  const tabItems = TABS.map(({ key, label, Icon }) => ({
    key,
    icon: Icon,
    label,
    badge: typeof counts[key] === 'number' ? counts[key] : undefined,
    badgeUnknown: counts[key] === null,
  }))

  return (
    <div>
      <PageHeader
        level={2}
        title="ติดตาม"
        subtitle="โน้ต · Recall · Ticket · ประวัติการโทรของคนไข้รายนี้"
        meta={lock.active && (
          <Badge tone={LOCK_TONE[lock.status] || 'slate'}>
            <Lock size={11} strokeWidth={2.5} />
            {LOCK_META[lock.status].label}
            {lock.byOther && <LockWho id={lock.by} />}
          </Badge>
        )}
        className="mb-5"
      />

      <PatientNotes patient={patient} setPatient={setPatient} me={me} />

      <Tabs items={tabItems} value={tab} onChange={setTab} className="mb-4" />

      {tab === 'recall' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          <RecallNotesSection patientId={Number(id)} meId={me?.employee_id} refreshKey={recallTick} />
        </div>
      )}

      {tab === 'ticket' && (
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Segmented options={TICKET_MODES} value={ticketMode} onChange={setTicketMode} />
            <Button variant="soft" size="sm" icon={Plus} onClick={() => setDrawer({ mode: 'create' })} className="ml-auto">
              สร้าง Ticket
            </Button>
          </div>
          <DataTable
            columns={TICKET_COLUMNS}
            {...ticketTable.tableProps}
            loading={tickets === null}
            error={ticketErr || null}
            onRetry={() => loadTickets()}
            empty={{ title: ticketMode === 'done' ? 'ยังไม่มี Ticket ที่เสร็จแล้ว' : 'ยังไม่มี Ticket ค้างของคนไข้รายนี้' }}
            onRowClick={(t) => setDrawer({ mode: 'view', ticketId: t.id })}
          />
        </div>
      )}

      {tab === 'phone' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
          {/* Exclusive: an errored call log must never also render as an empty one. */}
          {phoneErr
            ? <ErrorState message={phoneErr} onRetry={() => loadPhones()} />
            : phones === null ? <Loading /> : <PhoneLogTable rows={phoneRows} />}
        </div>
      )}

      {drawer && (
        <TicketDrawer
          mode={drawer.mode}
          ticketId={drawer.ticketId ?? null}
          branches={branches}
          tagCatalog={tagCatalog}
          initialPatient={drawer.mode === 'create' && patient
            ? { id: patient.id, name: patientLabel(patient), hn: patient.HN || patient.hn || '' }
            : null}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); loadTickets(true) /* quiet — no spinner blink, like every other refresh here */ }}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function LockWho({ id }) {
  const [code, setCode] = useState('')
  useEffect(() => {
    if (!id) return
    let alive = true
    cachedGet(`/api/employees/${id}`)
      .then((e) => { if (alive) setCode(e?.code || e?.nickname || e?.name || `#${id}`) })
      .catch(() => {})
    return () => { alive = false }
  }, [id])
  return code ? <span className="font-semibold">· {code}</span> : null
}

const patientLabel = (p) =>
  [p?.prefix, p?.name, p?.surname].filter(Boolean).join(' ').trim() || p?.fullname || `#${p?.id}`
